import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDtrData } from "../../hooks/dtrData";
import { useSidebarData } from "../../hooks/sidebarData";
import { destroySession } from "../../helpers/Session";
import axios from "../../api/axios";
import toast from "../sweetalert/Sweetalert";
import Swal from "sweetalert2";

const RecordAdjustmentManager = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    const { sidebarData } = useSidebarData();
    const { dtrData } = useDtrData();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const aimtoken = sessionStorage.getItem('aimtoken');
    const loggedUsername = sessionStorage.getItem('uname');
    const loggedDepartment = sessionStorage.getItem('department');
    const loggedDepartmentSlug = sessionStorage.getItem('departmentslug');
    const employeeUname = sessionStorage.getItem('timesheetEmployeeUname');
    const timesheetPayperiodId = sessionStorage.getItem('timesheetPayperiodId');

    const [updateDTR, setUpdateDTR] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [adjustmentId, setAdjustmentId] = useState(0);
    const [timein, setTimeIn] = useState(null);
    const [timeout, setTimeOut] = useState(null);
    const [startbreak, setStartBreak] = useState(null);
    const [stopbreak, setStopBreak] = useState(null);

    const [managedDepartmentAdjustment, setManagedDepartmentAdjustment] = useState(false);

    const adjustmentQueryKey = ['userDTRAdjustments', { uname: employeeUname, sidebarData }];

    const { data: adjustments, isLoading: isLoadingAdjustments, error: adjustmentError } = useQuery({
        queryKey: adjustmentQueryKey,
        queryFn: async () => {
            const res = await axios.get(`dtr/adjustments`, {
                params: {
                    uname: employeeUname,
                    date: sidebarData?.data?.rawdate,
                    dtrid: sidebarData?.data?.dtrid
                },
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!sidebarData?.data,
        retry: false,
    });

    useEffect(() => {
        if (adjustmentError?.response?.data.status === 'inactive') {
            destroySession();
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
        }
    }, [adjustmentError, navigate]);

    const { data: dtrStatus } = useQuery({
        queryKey: ['dtrstatus', { uname: employeeUname, currentPayperiod: timesheetPayperiodId }],
        enabled: !!timesheetPayperiodId
    });

    const { data: isAdjustmentApprover } = useQuery({
        queryKey: ['checkForAdjustmentApprover', loggedUsername],
        queryFn: async () => {
            const res = await axios.get(`approvals/departments?type=2&uname=${loggedUsername}`, {
                headers: { 
                    'Authorization': `Bearer ${accesstoken}`,
                    'X-Aim-Token': aimtoken 
                }
            });
            return res.data?.response;
        },
        enabled: !!loggedUsername && !!accesstoken && !!aimtoken
    });

    useEffect(() => {
        const checkDepartment = isAdjustmentApprover?.find(dept => dept.department_code === dtrData?.departmentcode);
        setManagedDepartmentAdjustment(checkDepartment);
    }, [isAdjustmentApprover]);

    const interpretStatus = (status) => {
        if (status === 1) return <span className="label label-success">APPROVED</span>;
        if (status === 2) return <span className="label label-danger">REJECTED</span>;
        return <span className="label label-primary">PENDING</span>;
    };

    const handleDtrUpdate = (id) => {
        const { rawtimein, rawtimeout, rawstartbreak, rawstopbreak } = sidebarData.data;
        setAdjustmentId(id);
        setTimeIn(rawtimein);
        setTimeOut(rawtimeout);
        setStartBreak(rawstartbreak);
        setStopBreak(rawstopbreak);
        setUpdateDTR(true);
    };

    const cancelDtrUpdate = () => {
        setAdjustmentId(0);
        setTimeIn(null);
        setTimeOut(null);
        setStartBreak(null);
        setStopBreak(null);
        setUpdateDTR(false);
        setSubmit(false);
    };

    const saveDtrUpdateMutation = useMutation({
        mutationFn: async (dtrUpdateData) => {
            const res = await axios.put('dtr/adjustments', dtrUpdateData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(adjustmentQueryKey);
            cancelDtrUpdate();
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || 'Something went wrong while updating DTR adjustment request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const saveDtrUpdate = () => {
        Swal.fire({
            text: 'Are you sure you want to APPROVE this adjustment request?',
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async (result) => {
                if (result) {
                    setSubmit(true);
                    await saveDtrUpdateMutation.mutateAsync({
                        adjustmentid: adjustmentId,
                        data: sidebarData.data?.rawdate,
                        dtrid: sidebarData.data?.dtrid,
                        timein,
                        timeout,
                        startbreak,
                        stopbreak,
                        uname: loggedUsername,
                        department: loggedDepartment,
                        departmentcode: loggedDepartmentSlug
                    });
                }
            }
        });
    };

    const rejectAdjustmentRequestMutation = useMutation({
        mutationFn: async (rejectData) => {
            const res = await axios.patch('dtr/adjustments', rejectData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(adjustmentQueryKey);
            cancelDtrUpdate();
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || 'Something went wrong while rejecting DTR adjustment request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleRejectAdjustmentRequest = (id) => {
        Swal.fire({
            text: 'Are you sure you want to REJECT this adjustment request?',
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async (result) => {
                if (result) {
                    setSubmit(true);
                    await rejectAdjustmentRequestMutation.mutateAsync({
                        adjustmentid: id,
                        uname: loggedUsername
                    });
                }
            }
        });
    };

    return (
        <div className="record-adjustment">
            <table className={sidebarData?.data?.class}>
                <tbody>
                    <tr>
                        <th>Day</th>
                        <td><span>{sidebarData?.data?.day}</span></td>
                    </tr>
                    <tr>
                        <th>Shift</th>
                        <td><span>{sidebarData?.data?.shift}</span></td>
                    </tr>
                    <tr>
                        <th>Time In</th>
                        <td>
                            <span>
                                {updateDTR ? (
                                    <input
                                        type="datetime-local"
                                        value={timein || ''}
                                        min={`${sidebarData?.data?.mindate}T00:00`}
                                        max={`${sidebarData?.data?.maxdate}T11:59`}
                                        onChange={(e) => setTimeIn(e.target.value)}
                                        disabled={submit}
                                    />
                                ) : (
                                    sidebarData?.data?.timein
                                )}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Time Out</th>
                        <td>
                            <span>
                                {updateDTR ? (
                                    <input
                                        type="datetime-local"
                                        value={timeout || ''}
                                        min={`${sidebarData?.data?.mindate}T00:00`}
                                        max={`${sidebarData?.data?.maxdate}T11:59`}
                                        onChange={(e) => setTimeOut(e.target.value)}
                                        disabled={submit}
                                    />
                                ) : (
                                    sidebarData?.data?.timeout
                                )}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Start Break</th>
                        <td>
                            <span>
                                {updateDTR ? (
                                    <input
                                        type="datetime-local"
                                        value={startbreak || ''}
                                        min={`${sidebarData?.data?.mindate}T00:00`}
                                        max={`${sidebarData?.data?.maxdate}T11:59`}
                                        onChange={(e) => setStartBreak(e.target.value)}
                                        disabled={submit}
                                    />
                                ) : (
                                    sidebarData?.data?.startbreak
                                )}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Stop Break</th>
                        <td>
                            <span>
                                {updateDTR ? (
                                    <input
                                        type="datetime-local"
                                        value={stopbreak || ''}
                                        min={`${sidebarData?.data?.mindate}T00:00`}
                                        max={`${sidebarData?.data?.maxdate}T11:59`}
                                        onChange={(e) => setStopBreak(e.target.value)}
                                        disabled={submit}
                                    />
                                ) : (
                                    sidebarData?.data?.stopbreak
                                )}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            {updateDTR && (
                <div className="buttons">
                    <button type="button" disabled={submit} onClick={saveDtrUpdate}>SAVE</button>
                    <button type="reset" disabled={submit} onClick={cancelDtrUpdate}>CANCEL</button>
                </div>
            )}

            <div className={`history ${isLoadingAdjustments ? 'loading' : ''}`}>
                {adjustments?.map((item) => (
                    <div className="details" key={item.id}>
                        {item.reason}
                        {interpretStatus(item.status)}
                        <span className="date">{item.datecreated}</span>
                        {item.status === 0 && !updateDTR && dtrStatus !== 2 && managedDepartmentAdjustment && (
                            <div className="buttons">
                                <button type="button" className="btn-success" title="APPROVE" onClick={() => handleDtrUpdate(item.id)}>
                                    <i className="fa fa-check"></i>
                                </button>
                                <button type="button" className="btn-danger" title="REJECT" onClick={() => handleRejectAdjustmentRequest(item.id)}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RecordAdjustmentManager;
